import { Text, useMask } from '@react-three/drei';
import { useEnsName } from 'wagmi';
import { useMemo, VoidFunctionComponent } from 'react';
import botIcon from '@/img/bot-icon.png';
import magicEdenIcon from '@/img/magic-eden-icon.png';
import downloadIcon from '@/img/download-icon.png';
import infoIcon from '@/img/info-icon.png';
import { Image } from '@/components/Image';
import { garamondNarrowBook } from '@/lib/fonts';
import { useResponsive } from '@/hooks/useResponsive';
import { offblack } from '@/lib/colors';
import { EditionIcon } from '@/components/scenes/edition/EditionIcon';
import { deviceProperties } from '@/lib/deviceProperties';
import { MobileBanner } from '@/components/scenes/edition/MobileBanner';
import { useRouterContext } from '@/context/RouterContext';
import { Address, MAGICEDEN_ITEMDETAILS_URL } from '@/lib/globals';

const isValidEthAddress = (address: string): boolean => {
    return address.match(/^0x[a-fA-F0-9]{40}$/) !== null;
};

const truncateAddress = (address: string): string => {
    if (!isValidEthAddress(address)) {
        return address.length >= 16 ? address.slice(10, 15) + '…' : address;
    }

    return address.slice(0, 7) + '…' + address.slice(-5);
};

interface Icon {
    src: string;
    onClick: () => void;
    aspect?: number;
    scale?: number;
}

interface Props {
    tokenOwner: Address;
    qrUrl: string;
    gltfUrl: string;
    tokenId: number;
    name: string;
}

export const OutsidePortal: VoidFunctionComponent<Props> = ({ tokenOwner, qrUrl, gltfUrl, name, tokenId }) => {
    const invertStencil = useMask(1, true);
    const { isTouchDevice } = deviceProperties();
    const { navigate } = useRouterContext();
    const { responsiveValue } = useResponsive();
    const iconSpace = responsiveValue(0.2, 0.29);
    const { data: ensName } = useEnsName({
        address: tokenOwner,
    });

    // icons ordered right to left
    const icons: Icon[] = useMemo(() => {
        const info = {
            src: infoIcon,
            onClick: () => window.open('/info', '_blank'),
        };
        const magicEden = {
            src: magicEdenIcon,
            onClick: () => window.open(`${MAGICEDEN_ITEMDETAILS_URL}/${tokenId}`, '_blank'),
        };
        const download = {
            src: downloadIcon,
            onClick: () => window.open(`/download?id=${encodeURIComponent(tokenId)}`, '_blank'),
        };
        const bot = {
            src: botIcon,
            onClick: () => navigate('/magicswan'),
            aspect: 172 / 119,
            scale: 0.85,
        };
        return [info, download, magicEden, bot];
    }, [navigate, tokenId]);

    return (
        <>
            {isTouchDevice ? (
                <MobileBanner gltfUrl={gltfUrl} name={name} tokenId={tokenId} />
            ) : (
                qrUrl && <Image color="#242424" scale={1} position={[-1.92, 0, 2]} transparent url={qrUrl} />
            )}
            <group position={responsiveValue([0, -1.89, 2], [-0.05, -1.92, 2])} scale={responsiveValue(1.1, 0.98)}>
                <Text
                    position={[responsiveValue(-1.07, -1.12), responsiveValue(0.015, -0.01), 0]}
                    anchorX="left"
                    font={garamondNarrowBook}
                    fontSize={responsiveValue(0.105, 0.115)}
                    letterSpacing={-0.03}
                    color={offblack}
                >
                    <meshBasicMaterial {...invertStencil} />
                    Owned by {ensName || truncateAddress(tokenOwner)}
                </Text>
                <group position={[responsiveValue(1.02, 1.13), responsiveValue(0.03, 0), 0]}>
                    {icons.map(({ src, onClick, aspect = 1, scale = 1 }, i) => (
                        <group key={i} position={[-iconSpace * i, 0, 0]}>
                            <EditionIcon src={src} onClick={onClick} aspect={aspect} scale={scale} key={i} />
                        </group>
                    ))}
                </group>
            </group>
        </>
    );
};
